import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { API_URL, authFetch } from "../../utils/auth";
import ToolSidebar from "../../utils/ToolSideBar";
import "../../../css/superuser/StaffDirectoryPage.css";
import "../../../css/staff/InternalWorkspace.css";

function SummaryCard({ label, value, hint }) {
  return (
    <article className="clients-metric-card">
      <span>{label}</span>
      <strong>{value || 0}</strong>
      {hint && <small>{hint}</small>}
    </article>
  );
}

function statusClass(client) {
  if (client.account_attention?.length > 0) return "needs-attention";
  if (client.active_project_count > 0 || client.status === "Active") return "active";
  return "past";
}

function projectCountLabel(count = 0) {
  return `${count} ${count === 1 ? "Project" : "Projects"}`;
}

function ClientMeta({ label, value, detail }) {
  return (
    <div className="client-meta-item">
      <span>{label}</span>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
    </div>
  );
}

function ClientCard({ client }) {
  const maintenance = client.maintenance || {};
  const hasAttention = client.account_attention?.length > 0;
  const badgeText = hasAttention ? "Needs Attention" : client.status || (client.active_project_count > 0 ? "Active" : "Past Client");
  const activeWorkDetail = client.active_project_name || client.current_project || "";
  const maintenanceDetail = maintenance.has_plan && (maintenance.usage_label || maintenance.status_label) ? (maintenance.usage_label || maintenance.status_label) : "";

  return (
    <article className="client-relationship-card">
      <div className="client-card-topline">
        <div className="client-identity">
          <h2>{client.client_name || client.business_name || "Client"}</h2>
          <p>{client.business_name || "Business not provided"}</p>
        </div>
        <span className={`client-status-badge ${statusClass(client)}`}>{badgeText}</span>
      </div>

      <div className="client-meta-grid">
        <ClientMeta label="Preferred Contact" value={client.preferred_contact_label || "No Preference"} detail={client.preferred_contact_warning || ""} />
        <ClientMeta label="Projects" value={projectCountLabel(client.project_count || 0)} />
        <ClientMeta label="Active Work" value={client.active_work_label || "No Active Projects"} detail={activeWorkDetail} />
        <ClientMeta label="Maintenance" value={maintenance.plan_name || "No active maintenance plan"} detail={maintenanceDetail} />
      </div>

      <div className="client-card-footer">
        {hasAttention ? <span className="client-attention-indicator">Needs Attention</span> : <span aria-hidden="true" />}
        <Link className="client-view-action" to={`/clients/${client.id}`}>View Client <span aria-hidden="true">?</span></Link>
      </div>
    </article>
  );
}

export default function ClientsPage() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [payload, setPayload] = useState({ results: [], summary: {}, filters: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const query = useMemo(() => {
    const next = new URLSearchParams();
    if (search) next.set("search", search);
    if (filter) next.set("filter", filter);
    return next.toString();
  }, [search, filter]);

  useEffect(() => {
    async function loadClients() {
      setLoading(true);
      try {
        const response = await authFetch(`${API_URL}/ops/clients/${query ? `?${query}` : ""}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || "Unable to load clients.");
        setPayload(data);
        setError("");
      } catch (err) {
        setError(err.message || "Unable to load clients.");
      } finally {
        setLoading(false);
      }
    }
    loadClients();
  }, [query]);

  return (
    <div className="staff-shell">
      <ToolSidebar active="clients" />
      <main className="staff-page internal-page clients-page">
        <header className="staff-header internal-hero clients-hero">
          <span>{payload.eyebrow || "CLIENT RELATIONSHIPS"}</span>
          <h1>{payload.heading || "Clients"}</h1>
          <p>{payload.supporting_text || "View client relationships, project history, maintenance coverage, and account activity."}</p>
        </header>

        <section className="clients-metric-grid" aria-label="Client summary metrics">
          <SummaryCard label="Total Clients" value={payload.summary?.total_clients} />
          <SummaryCard label="Active Projects" value={payload.summary?.active_projects} />
          <SummaryCard label="Maintenance Clients" value={payload.summary?.maintenance_clients} />
          <SummaryCard label="Needs Attention" value={payload.summary?.needs_attention} />
        </section>

        <section className="clients-toolbar-card" aria-label="Client search and filters">
          <input aria-label="Search clients" placeholder="Search clients, businesses, or email..." value={search} onChange={(event) => setSearch(event.target.value)} />
          <select aria-label="Client filter" value={filter} onChange={(event) => setFilter(event.target.value)}>
            {(payload.filters || [{ value: "", label: "All Clients" }]).map((item) => <option key={item.value} value={item.value}>{item.label === "All" ? "All Clients" : item.label}</option>)}
          </select>
        </section>

        <section className="clients-list-section" aria-label="Client relationships">
          {loading ? (
            <div className="clients-empty-state"><h2>Loading clients...</h2></div>
          ) : error ? (
            <div className="clients-empty-state error"><h2>Unable to load clients</h2><p>{error}</p></div>
          ) : payload.results.length === 0 ? (
            <div className="clients-empty-state"><h2>No clients found</h2><p>Try adjusting your search or filters.</p></div>
          ) : (
            <div className="clients-card-grid">{payload.results.map((client) => <ClientCard key={client.id} client={client} />)}</div>
          )}
        </section>
      </main>
    </div>
  );
}
